import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { useNavigate, useLocation } from 'react-router-dom';

import styles from './styles';

interface LocationState {
  orderId: string;
  orderNumber: string;
  total: number;
  items: any[];
  paymentId?: string;
  error?: string;
}

const BeerStoreSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  useEffect(() => {
    // Redirect to store if no order data
    if (!state?.orderId) {
      navigate('/beer-store');
    }
  }, [state, navigate]);

  const handleViewOrders = () => {
    navigate('/orders');
  };

  const handleContinueShopping = () => {
    navigate('/beer-store');
  };

  if (!state) {
    return null;
  }

  const { orderNumber, total, items, error } = state;

  return (
    <Box sx={styles.container}>
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          {/* Success Header */}
          <Card sx={styles.successCard}>
            <CardContent sx={styles.successContent}>
              <CheckCircleIcon sx={styles.successIcon} />
              <Typography variant="h4" sx={styles.successTitle}>
                Pedido Realizado com Sucesso!
              </Typography>
              <Typography variant="h6" sx={styles.orderNumber}>
                Pedido #{orderNumber}
              </Typography>
            </CardContent>
          </Card>

          {/* Error Alert if any */}
          {error && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Order Summary */}
          <Card sx={styles.summaryCard}>
            <CardContent>
              <Typography variant="h6" sx={styles.sectionTitle}>
                Resumo do Pedido
              </Typography>

              <Box sx={styles.orderInfo}>
                <Box sx={styles.infoRow}>
                  <Typography variant="body1">
                    <strong>Número do Pedido:</strong>
                  </Typography>
                  <Typography variant="body1">{orderNumber}</Typography>
                </Box>

                <Box sx={styles.infoRow}>
                  <Typography variant="body1">
                    <strong>Total Pago:</strong>
                  </Typography>
                  <Typography variant="body1" sx={styles.totalAmount}>
                    R$ {total.toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>

                <Box sx={styles.infoRow}>
                  <Typography variant="body1">
                    <strong>Itens:</strong>
                  </Typography>
                  <Typography variant="body1">
                    {items.length} produto(s)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Status Information */}
          <Card sx={styles.statusCard}>
            <CardContent>
              <Typography variant="h6" sx={styles.sectionTitle}>
                Status do Seu Pedido
              </Typography>

              <List sx={styles.statusList}>
                <ListItem sx={styles.statusItem}>
                  <ShoppingBagIcon sx={styles.statusIcon} />
                  <ListItemText
                    primary="Pedido Confirmado"
                    secondary="Seu pedido já está sendo processado em nosso sistema"
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>

                <Divider sx={styles.statusDivider} />

                <ListItem sx={styles.statusItem}>
                  <NotificationsIcon sx={styles.statusIcon} />
                  <ListItemText
                    primary="Notificações Automáticas"
                    secondary="Você receberá uma mensagem toda vez que o status do seu pedido for alterado"
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>

                <Divider sx={styles.statusDivider} />

                <ListItem sx={styles.statusItem}>
                  <TrackChangesIcon sx={styles.statusIcon} />
                  <ListItemText
                    primary="Acompanhe Seu Pedido"
                    secondary="Você pode acompanhar a evolução do seu pedido a qualquer momento através da seção 'Meus Pedidos'"
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={styles.actionButtons}>
            <Button
              variant="outlined"
              onClick={handleViewOrders}
              sx={styles.viewOrdersButton}
              size="large"
            >
              Ver Meus Pedidos
            </Button>

            <Button
              variant="contained"
              onClick={handleContinueShopping}
              sx={styles.continueShoppingButton}
              size="large"
            >
              Continuar Comprando
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BeerStoreSuccess;
